import type { StoredCommercialRequest } from "../types";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button as MuiButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import { Link as RouterLink } from "react-router-dom";
import { useMemo } from "react";
import { CommercialBookDataGrid } from "../../../components/DataGrid";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridRowId,
} from "@mui/x-data-grid-pro";
import { ButtonVariantOption, LinkButton } from "../../../components/Button";
import { SizingWrapperStyle } from "../../../components/Wrapper";
import { format, isValid } from "date-fns";
import { useCommercialZipDownloadUrl } from "@/features/commercialBookStatus/api/getZipDownloadUrl";
import { useFeatureFlags } from "@/configs/featureFlag";
import {
  OTHER_USER_RESOURCE_LABEL,
  makeOtherUserResourceColumn,
} from "./otherUserResourceColumn";

interface CommercialStatusDataGridRow {
  id: number;
  requestedAt: Date;
  requestPictureCount: number;
  successPictureCount: number;
  /** 先頭明細の法人名（明細0件・未取得時はnull）。一覧で法人名を既定表示するために使う */
  firstCompanyName?: string | null;
  status: string;
  isOtherUserResource: boolean;
  action?: boolean;
}

/**
 * 取得依頼一覧に表示する法人名の要約。1依頼は複数社を含むため、先頭1件＋「他N件」で表す。
 * @param firstCompanyName 先頭明細の法人名
 * @param count 依頼に含まれる登記の件数
 * @returns 例「株式会社アルファ商事 他2件」。法人名が無ければ空文字
 */
export const formatRequestCompanySummary = (
  firstCompanyName: string | null | undefined,
  count: number
): string => {
  if (firstCompanyName == null || firstCompanyName === "") return "";
  const others = count - 1;
  return others > 0 ? `${firstCompanyName} 他${others}件` : firstCompanyName;
};

/** 取得依頼の取得状況コードと表示名の対応 */
export const COMMERCIAL_PICTURE_REQUEST_STATUS_LABELS: Record<string, string> =
  {
    REQUESTING: "依頼中",
    SUCCESS: "成功",
    SOME_FAILED: "失敗あり",
    COMPLETED: "完了",
  };

/** 取得状況のチップ色。未定義（完了）は輪郭のみの中立表示にする */
export const COMMERCIAL_PICTURE_REQUEST_STATUS_CHIP_COLORS: Record<
  string,
  "info" | "success" | "warning"
> = {
  REQUESTING: "info",
  SUCCESS: "success",
  SOME_FAILED: "warning",
};

const StyledDataGrid = styled(CommercialBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

/**
 * 一括ダウンロードができない理由を返す（ダウンロード可能な場合は空文字）
 *
 * 非活性の理由をツールチップでそのまま表示するため、判定と文言を1箇所にまとめている。
 */
const getDownloadDisabledReason = (
  row: CommercialStatusDataGridRow
): string => {
  // ZIPは全明細の取得完了後に作成されるため、依頼中はダウンロードさせない
  if (row.status === "REQUESTING") {
    return "取得完了後にダウンロードが可能になります。";
  }
  // ZIPには取得に成功した登記だけが入るため、成功が0件なら中身が空になる。
  // 取得数が0件の場合と全件失敗した場合の両方がここに該当する
  // （全件失敗でもstatusはSOME_FAILEDになり、statusだけでは判別できない）
  if (row.successPictureCount === 0) {
    return "取得に成功した登記がないためダウンロードできません。";
  }
  return "";
};

const makeGridColDef = (
  handleDownload: (id: GridRowId) => Promise<void>,
  isZipDownloadEnabled: boolean,
  isSharable: boolean
): GridColDef[] => {
  return [
    {
      field: "id",
      headerName: "No",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "requestedAt",
      headerName: "商業登記依頼日",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value === undefined) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: "firstCompanyName",
      headerName: "法人名",
      description: "取得した登記の先頭1件の法人名（詳細で全件を確認できます）",
      minWidth: 260,
      flex: 1,
      sortable: false,
      renderCell: ({
        row,
      }: GridRenderCellParams<string, CommercialStatusDataGridRow>) => {
        const summary = formatRequestCompanySummary(
          row.firstCompanyName,
          row.requestPictureCount
        );
        return summary === "" ? "—" : summary;
      },
    },
    {
      field: "requestPictureCount",
      headerName: "取得数",
      description: "取得を依頼した登記の件数",
      width: 120,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }: GridRenderCellParams<number>) => {
        if (value === undefined) return "";
        return `${value}件`;
      },
    },
    {
      field: "status",
      headerName: "取得状況",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value === undefined) return "";
        const label = COMMERCIAL_PICTURE_REQUEST_STATUS_LABELS[value] ?? value;
        const color = COMMERCIAL_PICTURE_REQUEST_STATUS_CHIP_COLORS[value];
        return (
          <Chip
            label={label}
            size="small"
            color={color ?? "default"}
            variant={color === undefined ? "outlined" : "filled"}
          />
        );
      },
    },
    ...(isSharable ? [makeOtherUserResourceColumn()] : []),
    {
      field: "action",
      headerName: isZipDownloadEnabled ? "操作" : "詳細",
      minWidth: isZipDownloadEnabled ? 280 : 110,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        id,
        row,
      }: GridRenderCellParams<string, CommercialStatusDataGridRow>) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <LinkButton
              variant={ButtonVariantOption.Outlined}
              size="small"
              wrapperSize={SizingWrapperStyle.INHERIT}
              href={`/commercialbookstatus/${id}/?stored=1`}
            >
              詳細
            </LinkButton>
            {isZipDownloadEnabled ? (
              <Tooltip title={getDownloadDisabledReason(row)}>
                <span>
                  <MuiButton
                    variant={ButtonVariantOption.Outlined}
                    color="brand"
                    size="small"
                    onClick={() => {
                      void handleDownload(id);
                    }}
                    startIcon={<DownloadIcon />}
                    disabled={getDownloadDisabledReason(row) !== ""}
                    sx={{
                      borderRadius: "3px",
                      textTransform: "none",
                      fontWeight: 700,
                      transition: "all 0.15s ease",
                    }}
                  >
                    ダウンロード
                  </MuiButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        );
      },
    },
  ];
};

/** 絞り込み条件に一致する取得依頼がないときにグリッド内へ表示する文言 */
export const NO_COMMERCIAL_PICTURE_REQUEST_MESSAGE =
  "該当する取得依頼はありません";

const NoRowsOverlay: React.FC = () => (
  <Stack height="100%" alignItems="center" justifyContent="center">
    <Typography>{NO_COMMERCIAL_PICTURE_REQUEST_MESSAGE}</Typography>
  </Stack>
);

/** 取得依頼の取得状況チップ（テーブル/カード共通） */
const RequestStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const label = COMMERCIAL_PICTURE_REQUEST_STATUS_LABELS[status] ?? status;
  const color = COMMERCIAL_PICTURE_REQUEST_STATUS_CHIP_COLORS[status];
  return (
    <Chip
      label={label}
      size="small"
      color={color ?? "default"}
      variant={color === undefined ? "outlined" : "filled"}
    />
  );
};

/** カード内の1項目（ラベル＋値）を横並びで表示する */
const CardRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 1,
      py: 0.25,
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ textAlign: "right" }}>{children}</Box>
  </Box>
);

/**
 * SP（狭幅）向けの取得依頼カード一覧。テーブルは横スクロールで読みづらいため、
 * 1依頼=1カードで縦に並べる。ページ送りは一覧の下の共通ページ送りが担う。
 */
const MobileRequestCards: React.FC<{
  rows: CommercialStatusDataGridRow[];
  loading: boolean;
  isSharable: boolean;
}> = ({ rows, loading, isSharable }) => {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (rows.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography>{NO_COMMERCIAL_PICTURE_REQUEST_MESSAGE}</Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={1.5}>
      {rows.map((row) => (
        <Card key={row.id} variant="outlined">
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                No. {row.id}
              </Typography>
              <RequestStatusChip status={row.status} />
            </Box>
            <CardRow label="法人名">
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, wordBreak: "break-all" }}
              >
                {formatRequestCompanySummary(
                  row.firstCompanyName,
                  row.requestPictureCount
                ) || "—"}
              </Typography>
            </CardRow>
            <CardRow label="商業登記依頼日">
              <Typography variant="body2">
                {isValid(new Date(row.requestedAt))
                  ? format(new Date(row.requestedAt), "yyyy/MM/dd HH:mm")
                  : ""}
              </Typography>
            </CardRow>
            <CardRow label="取得数">
              <Typography variant="body2">
                {row.requestPictureCount}件
              </Typography>
            </CardRow>
            {isSharable && row.isOtherUserResource && (
              <CardRow label="取得ユーザー">
                <Typography variant="body2">
                  {OTHER_USER_RESOURCE_LABEL}
                </Typography>
              </CardRow>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mt: 1,
              }}
            >
              <MuiButton
                component={RouterLink}
                to={`/commercialbookstatus/${row.id}/?stored=1`}
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                aria-label="この取得依頼の詳細（登記一覧）を見る"
              >
                詳細
              </MuiButton>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

interface FeedGridProps {
  /** 取得依頼の検索結果（表示中のページ分） */
  rows: StoredCommercialRequest[];
  loading: boolean;
  /** 共有ONの会社か。ONなら「他ユーザー取得」を表示する */
  isSharable: boolean;
}

export const FeedGrid: React.FC<FeedGridProps> = ({
  rows,
  loading,
  isSharable,
}) => {
  const { handleDownload } = useCommercialZipDownloadUrl();
  // NOTE: commercialBookZipDownloadは、BEのリリースと遡及生成の完了後にONにする
  const { commercialBookZipDownload } = useFeatureFlags();
  const isZipDownloadEnabled = commercialBookZipDownload === true;
  const colDef = useMemo(
    () => makeGridColDef(handleDownload, isZipDownloadEnabled, isSharable),
    [handleDownload, isZipDownloadEnabled, isSharable]
  );
  const theme = useTheme();
  // 狭幅（md未満＝タブレット縦・スマホ）はテーブルでなくカードで表示する
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobile) {
    return (
      <MobileRequestCards
        rows={rows}
        loading={loading}
        isSharable={isSharable}
      />
    );
  }

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={rows}
      pagination={false}
      hideFooter
      columns={colDef}
      loading={loading}
      components={{ NoRowsOverlay }}
      sx={{ "--DataGrid-overlayHeight": "120px" }}
    />
  );
};
